import type {
  ClusterHPA,
  HPAConversionResultStatus,
  HPATargetWorkloadContainer,
  WorkloadOptimizationMetricSpec as MetricSpec,
} from '@cast/types';

import type {
  HpaContainerRow,
  HpaConversionStatus,
  HpaManagedStatus,
  HpaMetric,
  HpaOwner,
  HpaRow,
  HpaRowState,
  HpaScope,
  HpaSummary,
  MetricTypeDistribution,
} from './_types';

// ─── Metric classification ──────────────────────────────────────────────────

function classifyMetrics(metrics: MetricSpec[]): {
  metric: HpaMetric | null;
  scope: HpaScope | null;
} {
  if (!metrics.length) return { metric: null, scope: null };

  const hasContainerResource = metrics.some(
    (m) => m.type === 'CONTAINER_RESOURCE'
  );
  const hasPodResource = metrics.some((m) => m.type === 'RESOURCE');
  const hasExternal = metrics.some(
    (m) => m.type === 'EXTERNAL' || m.type === 'PODS' || m.type === 'OBJECT'
  );

  const scope: HpaScope = hasContainerResource
    ? hasPodResource
      ? 'mixed'
      : 'container'
    : 'pod';

  const resourceNames = new Set<string>();
  for (const m of metrics) {
    if (m.type === 'RESOURCE' && m.resource) {
      resourceNames.add(m.resource.name);
    } else if (m.type === 'CONTAINER_RESOURCE' && m.containerResource) {
      resourceNames.add(m.containerResource.name);
    }
  }

  let metric: HpaMetric | null;
  if (hasExternal && resourceNames.size === 0) {
    metric = 'external';
  } else if (hasExternal) {
    metric = 'mixed';
  } else if (resourceNames.has('cpu') && resourceNames.has('memory')) {
    metric = 'cpu+memory';
  } else if (resourceNames.has('cpu')) {
    metric = 'cpu';
  } else if (resourceNames.has('memory')) {
    metric = 'memory';
  } else {
    metric = 'mixed';
  }

  return { metric, scope };
}

// ─── Target extraction ──────────────────────────────────────────────────────

function extractPodUtilizationTarget(metrics: MetricSpec[]): number | null {
  const match = metrics.find(
    (m) => m.type === 'RESOURCE' && m.resource?.target.type === 'UTILIZATION'
  );
  return match?.resource ? parseInt(match.resource.target.value, 10) : null;
}

function extractPodAvgValueTarget(
  metrics: MetricSpec[],
  resourceName?: string
): string | null {
  const match = metrics.find(
    (m) =>
      m.type === 'RESOURCE' &&
      m.resource?.target.type === 'AVERAGE_VALUE' &&
      (resourceName == null || m.resource?.name === resourceName)
  );
  return match?.resource?.target.value ?? null;
}

function containerUtilizationTarget(
  metrics: MetricSpec[],
  containerName: string
): number | null {
  const match = metrics.find(
    (m) =>
      m.type === 'CONTAINER_RESOURCE' &&
      m.containerResource?.container === containerName &&
      m.containerResource.target.type === 'UTILIZATION'
  );
  return match?.containerResource
    ? parseInt(match.containerResource.target.value, 10)
    : null;
}

// ─── Conversion status mapping ──────────────────────────────────────────────

function mapConversionStatus(
  status: HPAConversionResultStatus
): HpaConversionStatus {
  switch (status) {
    case 'CONVERTED':
      return 'converted';
    case 'ALREADY_AVERAGE':
      return 'already-average';
    case 'MISSING_REQUESTS':
    case 'REQUESTS_TOO_SMALL':
      return 'missing-requests';
    case 'NO_METRICS':
      return 'no-metrics';
    default:
      return 'skipped';
  }
}

// ─── Managed status ─────────────────────────────────────────────────────────

function deriveManagedStatus(item: ClusterHPA): HpaManagedStatus | null {
  if (item.management.mode !== 'MODE_MANAGED') return 'unmanaged';
  if (item.management.source === 'SOURCE_SCALING_POLICY')
    return 'hpa-scaling-policy';
  if (item.management.source === 'SOURCE_VPA_CONVERTER') return 'vpa-converter';
  return null;
}

// ─── Owner mapping ──────────────────────────────────────────────────────────

function mapOwner(type: string): HpaOwner {
  switch (type) {
    case 'TYPE_KEDA':
      return 'keda';
    case 'TYPE_CASTAI':
      return 'castai';
    default:
      return 'native';
  }
}

// ─── Container rows ─────────────────────────────────────────────────────────

function buildContainerRows({
  containers,
  originalMetrics,
  parentMetric,
  maxingOutPercent,
  parentState,
}: {
  containers: HPATargetWorkloadContainer[];
  originalMetrics: MetricSpec[];
  parentMetric: HpaMetric | null;
  maxingOutPercent: number | null;
  parentState: HpaRowState;
}): HpaContainerRow[] {
  const containerScopedNames = new Set(
    originalMetrics
      .filter((m) => m.type === 'CONTAINER_RESOURCE')
      .map((m) => m.containerResource?.container)
      .filter(Boolean) as string[]
  );

  return containers.map((c): HpaContainerRow => {
    const isContainerScoped = containerScopedNames.has(c.name);
    const scope: HpaScope = isContainerScoped ? 'container' : 'pod';

    const containerMetricNames = new Set(
      originalMetrics
        .filter(
          (m) =>
            m.type === 'CONTAINER_RESOURCE' &&
            m.containerResource?.container === c.name
        )
        .map((m) => m.containerResource?.name)
        .filter(Boolean)
    );
    let metric: HpaMetric = parentMetric ?? 'mixed';
    if (containerMetricNames.has('cpu') && containerMetricNames.has('memory')) {
      metric = 'cpu+memory';
    } else if (containerMetricNames.has('memory')) {
      metric = 'memory';
    } else if (containerMetricNames.has('cpu')) {
      metric = 'cpu';
    }

    return {
      type: 'container',
      containerName: c.name,
      metric,
      scope,
      maxingOutPercent,
      hpaTargetPercent: containerUtilizationTarget(originalMetrics, c.name),
      currentCpuReq: c.resources?.requests?.cpuCores ?? null,
      currentMemReq: c.resources?.requests?.memoryGib ?? null,
      originalCpuReq: c.originalResources?.requests?.cpuCores ?? null,
      originalMemReq: c.originalResources?.requests?.memoryGib ?? null,
      recCpuReq: c.recommendation?.requests?.cpuCores ?? null,
      recMemReq: c.recommendation?.requests?.memoryGib ?? null,
      state: parentState,
    };
  });
}

// ─── Main transform ─────────────────────────────────────────────────────────

export function transformClusterHPA(item: ClusterHPA): HpaRow {
  const originalMetrics = item.original.metrics ?? [];
  const inClusterMetrics = item.inCluster.metrics ?? [];
  const { metric, scope } = classifyMetrics(originalMetrics);

  const minReplicas = item.inCluster.minReplicas ?? 0;
  const maxReplicas = item.inCluster.maxReplicas;
  const range = maxReplicas - minReplicas;
  const maxingOutPercent =
    range > 0
      ? Math.min(
          100,
          Math.max(
            0,
            Math.round(
              ((item.workload.runningPods - minReplicas) / range) * 100
            )
          )
        )
      : null;

  const state: HpaRowState =
    maxingOutPercent !== null && maxingOutPercent >= 95
      ? 'maxing-out'
      : 'normal';

  const isManagedByVpaConverter =
    item.management.mode === 'MODE_MANAGED' &&
    item.management.source === 'SOURCE_VPA_CONVERTER';

  const conversion: HpaConversionStatus | null = isManagedByVpaConverter
    ? 'converted'
    : item.conversion
    ? mapConversionStatus(item.conversion.status)
    : null;

  const convertedMetrics =
    item.conversion?.status === 'CONVERTED' ||
    item.conversion?.status === 'ALREADY_AVERAGE'
      ? item.conversion.converted?.metrics ?? null
      : null;
  const primaryResourceName =
    originalMetrics.find((m) => m.type === 'RESOURCE')?.resource?.name ?? null;
  const conversionTargetValue =
    convertedMetrics && primaryResourceName
      ? extractPodAvgValueTarget(convertedMetrics, primaryResourceName)
      : null;

  return {
    type: 'workload',
    hpaName: item.name,
    namespace: item.namespace,
    workloadId: item.workload.id,
    workloadName: item.workload.name,
    kind: item.workload.kind,
    ownerName: item.owner.name ?? '',
    ownerKind: item.owner.kind ?? '',
    scalingPolicyName: item.workload.scalingPolicyName || null,
    owner: mapOwner(item.owner.type),
    scope: scope ?? null,
    metric: metric ?? null,
    conversion,
    managedStatus: deriveManagedStatus(item),
    origMinReplicas: item.original.minReplicas ?? null,
    origMaxReplicas: item.original.maxReplicas,
    origManagedByCastai: item.original.managedByCastai,
    origTargetCpuPct: item.original.targetCpuUtilizationPercentage ?? null,
    inClusterMinReplicas: item.inCluster.minReplicas ?? null,
    inClusterMaxReplicas: item.inCluster.maxReplicas,
    inClusterManagedByCastai: item.inCluster.managedByCastai,
    inClusterTargetCpuPct:
      item.inCluster.targetCpuUtilizationPercentage ?? null,
    currentReplicas: item.workload.runningPods,
    maxingOutPercent,
    hpaTargetPercent: extractPodUtilizationTarget(originalMetrics),
    hpaAvgValue:
      extractPodAvgValueTarget(originalMetrics) ??
      extractPodAvgValueTarget(inClusterMetrics) ??
      extractPodAvgValueTarget(convertedMetrics ?? []),
    conversionTargetValue,
    conversionError: item.conversion?.errorMessage ?? null,
    state,
    containers: buildContainerRows({
      containers: item.workload.containers,
      originalMetrics,
      parentMetric: metric,
      maxingOutPercent,
      parentState: state,
    }),
  };
}

export function transformListClusterHPAsResponse(
  items: ClusterHPA[]
): HpaRow[] {
  return items.map(transformClusterHPA);
}

export function computeHpaSummary(rows: HpaRow[]): HpaSummary {
  const total = rows.length;
  const maxingOut = rows.filter((r) => r.state === 'maxing-out').length;
  const keda = rows.filter((r) => r.owner === 'keda').length;
  const converted = rows.filter(
    (r) => r.conversion === 'converted' || r.conversion === 'already-average'
  ).length;
  const skipped = rows.filter(
    (r) =>
      r.conversion !== null &&
      r.conversion !== 'converted' &&
      r.conversion !== 'already-average'
  ).length;

  const namespaces = new Set(rows.map((r) => r.namespace)).size;

  const metricCounts: Record<string, number> = {};
  for (const row of rows) {
    const key = row.metric ?? 'mixed';
    metricCounts[key] = (metricCounts[key] ?? 0) + 1;
  }
  const metricDistribution: MetricTypeDistribution[] = [
    { key: 'cpu', label: 'CPU', subtitle: 'Resource cpu' },
    { key: 'memory', label: 'Memory', subtitle: 'Resource memory' },
    { key: 'cpu+memory', label: 'CPU+Mem', subtitle: 'ContainerResource' },
    {
      key: 'external',
      label: 'External',
      subtitle: 'Pods / Object / External',
    },
    { key: 'mixed', label: 'Mixed', subtitle: 'Multiple metric types' },
  ]
    .map((m) => ({ ...m, count: metricCounts[m.key] ?? 0 }))
    .filter((m) => m.count > 0);

  const lowUtilizationHpas = rows
    .filter((r) => r.hpaTargetPercent !== null && r.hpaTargetPercent < 60)
    .sort((a, b) => (a.hpaTargetPercent ?? 0) - (b.hpaTargetPercent ?? 0));

  return {
    total,
    namespaces,
    alerts: {
      maxingOut: {
        count: maxingOut,
        total,
        subtitle: '≥95% of replicas in use',
      },
      keda: { count: keda, total, subtitle: 'KEDA-managed HPAs' },
      converted: { count: converted, total, subtitle: 'Conversion enabled' },
      skipped: { count: skipped, total, subtitle: 'Conversion skipped' },
    },
    metricDistribution,
    lowUtilizationHpas,
  };
}
